import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  ClipboardCheck,
  ClipboardList,
  CheckCircle,
  Clock,
  Star,
  CalendarClock,
  AlertTriangle,
  XCircle,
} from 'lucide-react';
import { supabase } from '../../services/supabase';
import { listaEnCastellano } from '../../lib/leerVarias';
import {
  cuantasAsignadas,
  desgloseDeLaCola,
  titularDeLaCola,
  type PendienteDeEvaluar,
} from '../../lib/evaluationPendientes';
import {
  quienLleva,
  esCanceladaPorElClub,
  ETIQUETA_CANCELADA,
  COLORES_CANCELADA,
  type TrainerSessionRow,
} from '../../lib/quienLleva';
import { useAuthStore } from '../../store/authStore';
import MonitorVideoAnalysisPanel from '../../components/MonitorVideoAnalysisPanel';
import { diaDe, hoy } from '../../lib/fechaLocal';

interface TodaySession {
  id: string;
  time_start: string;
  time_end: string;
  group_name: string;
  confirmed: number;
  pending: number;
  /**
   * La canceló el club. ⚠️ EL DATO YA LLEGABA Y SE TIRABA AQUÍ:
   * `get_trainer_sessions` devuelve `status` y `TrainerSessionRow` lo declara,
   * pero este modelo de vista no lo copiaba. Medido en producción: las 7
   * sesiones canceladas del club decían «0 alumnos · Listo».
   */
  cancelada: boolean;
}

interface PendingTask {
  id: string;
  date: string;
  time_start: string;
  time_end: string;
  group_name: string;
  pending_attendance: number;
  pending_rating: number;
}

// Máximo de sesiones pendientes que se listan en el dashboard. El contador usa
// el total REAL (pendingTotal); este tope es solo de presentación.
const MAX_VISIBLE_TASKS = 5;

// Este dashboard es el GEMELO en panel de MonitorHomeScreen (app), arreglado en
// el lote 3. Tenía el mismo defecto y la misma cascada: `groups` alimenta a
// todo lo demás, así que un fallo en su lectura no dejaba un hueco — dejaba un
// entrenador "sin grupos", con los tres contadores a 0 y un visto verde de
// «sin sesiones hoy». postgrest-js no rechaza nunca: el error llegaba como
// lista vacía.
const QUE = {
  // Una sola lectura para las dos listas (la RPC de las tres vías), así que una
  // sola etiqueta: antes eran tres —`tus grupos`, `las sesiones de hoy` y `las
  // sesiones pasadas`— porque eran tres consultas encadenadas.
  sesiones: 'tus sesiones',
  asistencias: 'las asistencias de hoy',
  notas: 'las notas rápidas',
  evaluaciones: 'las evaluaciones pendientes',
  pf: 'los jugadores de preparación física sin planificar',
} as const;

export default function MonitorDashboardPage() {
  const { profile } = useAuthStore();
  const navigate = useNavigate();
  const [todaySessions, setTodaySessions] = useState<TodaySession[]>([]);
  const [pendingTasks, setPendingTasks] = useState<PendingTask[]>([]);
  // `null` = no se ha podido contar. Es distinto de un 0 contado, y en un
  // panel de tareas pendientes esa diferencia es todo: un 0 dice «no tienes
  // nada que hacer».
  const [pendingTotal, setPendingTotal] = useState<number | null>(0);
  const [pendingEvals, setPendingEvals] = useState<number | null>(0);
  const [pendingAsignadas, setPendingAsignadas] = useState(0);
  const [pfUnplanned, setPfUnplanned] = useState<number | null>(0);
  const [loading, setLoading] = useState(true);
  const [bloquesFallidos, setBloquesFallidos] = useState<string[]>([]);
  // Las sesiones de hoy son una lista, no un contador: hace falta saber si el
  // vacío es de verdad para no pintar el visto verde de «sin sesiones hoy».
  const [hoyIlegible, setHoyIlegible] = useState(false);

  const load = useCallback(async () => {
    if (!profile) return;

    // El día de calendario LOCAL, que es lo que entiende una columna `date`.
    // `toISOString()` daría la fecha UTC: de 00:00 a 02:00, ayer. Ver §140.
    const today = hoy();

    // PF sin planificar (48h): RPC role-aware → el monitor recibe solo sus
    // jugadores GYM asignados sin rutina publicada en hoy..hoy+2. Se calcula
    // ANTES del early-return por "sin grupos" para que un entrenador solo-PF
    // (sin grupos colectivos) también vea este aviso en su dashboard.
    const fallidos: string[] = [];
    setHoyIlegible(false);

    const { data: unplannedRaw, error: errPf } = await supabase.rpc('gym_players_unplanned_48h');
    if (errPf) fallidos.push(QUE.pf);
    setPfUnplanned(errPf ? null : ((unplannedRaw ?? []) as { user_id: string }[]).length);

    // mig 313/314 · UNA sola lectura para las dos listas, y por las TRES VÍAS.
    //
    // Esto filtraba por el `group_id` de mis grupos activos, que es la regla de
    // una sola vía que el SQL ya había retirado: le ponía al entrenador las
    // sesiones de su grupo que dio OTRO, y le escondía las que son suyas por
    // override de la sesión o por clase especial. Medido en producción antes de
    // tocarlo: 3 de las que le salían a Alex las dio Robert, y esas mismas 3 no
    // le salían a Robert.
    //
    // `get_trainer_sessions` devuelve el club entero del rango con el
    // entrenador EFECTIVO resuelto en el servidor —override → clase especial →
    // grupo— y aquí se queda solo lo mío: este panel contesta «qué me falta a
    // mí», no «qué falta en el club». La lista del club vive en la Agenda.
    //
    // Y de paso se va la cascada por grupos: un entrenador SIN grupos activos
    // pero con sesiones asignadas ya no se queda el panel en blanco, que es lo
    // que pasaba con el early-return de `groups`.
    const desde = new Date();
    desde.setDate(desde.getDate() - 60);
    const desdeStr = diaDe(desde);

    const { data: filasRpc, error: errSesiones } = await supabase.rpc('get_trainer_sessions', {
      p_trainer_id: profile.id,
      p_from: desdeStr,
      p_to: today,
    });

    // ⚠️ Un fallo aquí NO puede dejar los contadores a 0: un 0 se lee como «no
    // te queda nada por hacer». Se marca como no contado. La cola de
    // evaluaciones se pide igual más abajo, porque no depende de esta lectura —
    // antes sí, por estar detrás del early-return de los grupos.
    if (errSesiones) {
      fallidos.push(QUE.sesiones);
      setHoyIlegible(true);
      setTodaySessions([]);
      setPendingTasks([]);
      setPendingTotal(null);
    }

    const mias = errSesiones
      ? []
      : ((filasRpc as unknown as TrainerSessionRow[] | null) ?? []).filter(
          (f) => quienLleva(f, profile.id).mia
        );

    const todayData = mias
      .filter((f) => f.date === today)
      .sort((a, b) => a.time_start.localeCompare(b.time_start));

    // `p_to` es inclusivo, así que las de hoy hay que sacarlas de las pasadas.
    // De la más reciente hacia atrás, que es como se recupera lo que quedó sin
    // pasar.
    const pastSessions = mias
      .filter((f) => f.date < today)
      .sort((a, b) =>
        a.date === b.date ? a.time_start.localeCompare(b.time_start) : b.date.localeCompare(a.date)
      );

    if (todayData.length) {
      const todayIds = todayData.map((s) => s.id);
      const { data: enrollData, error: errEnroll } = await supabase
        .from('enrollments')
        .select('session_id, attendance')
        .in('session_id', todayIds)
        .eq('status', 'confirmed');

      // Sin las asistencias, «0 alumnos» y «Listo» son mentira: la sesión de
      // hoy sigue mereciendo salir, pero el recuento no se afirma.
      if (errEnroll) fallidos.push(QUE.asistencias);

      type EnrollAttend = { session_id: string; attendance: boolean | null };
      const countMap: Record<string, { confirmed: number; pending: number }> = {};
      for (const e of (enrollData ?? []) as EnrollAttend[]) {
        if (!countMap[e.session_id]) countMap[e.session_id] = { confirmed: 0, pending: 0 };
        countMap[e.session_id].confirmed++;
        if (e.attendance === null) countMap[e.session_id].pending++;
      }

      setTodaySessions(
        todayData.map((s) => ({
          id: s.id,
          time_start: s.time_start,
          time_end: s.time_end,
          group_name: s.title,
          confirmed: countMap[s.id]?.confirmed ?? 0,
          pending: countMap[s.id]?.pending ?? 0,
          cancelada: esCanceladaPorElClub(s),
        }))
      );
    }

    // Pendientes: las MÍAS de los últimos 60 días con trabajo sin terminar.
    // La lista y su rango salen de la lectura de arriba; el fallo de esa
    // lectura ya dejó el contador en «no contado», que es distinto de un 0.
    if (pastSessions.length) {
      const pastIds = pastSessions.map((s) => s.id);

      const [enrollRes, notesRes] = await Promise.all([
        supabase
          .from('enrollments')
          .select('session_id, user_id, attendance')
          .in('session_id', pastIds)
          .eq('status', 'confirmed'),
        supabase
          .from('quick_notes')
          .select('session_id, student_id')
          .in('session_id', pastIds)
          .eq('trainer_id', profile.id),
      ]);
      const { data: enrollData } = enrollRes;
      const { data: evalData } = notesRes;

      // Los dos alimentan el MISMO contador y desde lados opuestos: sin
      // inscripciones no hay asistencias que falten, y sin notas todas
      // parecerían puestas. Cualquiera de los dos fallando invalida ESE número
      // —el de tareas— y solo ese.
      //
      // ⚠️ AQUÍ HABÍA UN `return`, y el comentario de arriba afirmaba lo
      // contrario de lo que hacía el código: decía «el MISMO contador» y acto
      // seguido ponía `setPendingEvals(null)` y se iba. Las evaluaciones
      // formales NO salen de estas dos lecturas: salen de una RPC propia que
      // ni se llegaba a pedir. Un fallo leyendo las notas rápidas apagaba la
      // cola de evaluaciones —y con ella el encargo de dirección— por nada.
      //
      // Es el mismo fallo que la mig 313 ya quitó del gemelo de la app
      // (`MonitorHomeScreen`), que por eso no lo tiene: las lecturas se
      // acumulan y ninguna decide por las demás.
      if (enrollRes.error || notesRes.error) {
        if (enrollRes.error) fallidos.push(QUE.asistencias);
        if (notesRes.error) fallidos.push(QUE.notas);
        setPendingTotal(null);
        setPendingTasks([]);
      } else {
        // PR-EV1: "pending_rating" pasa a ser "notas rápidas pendientes".
        type NoteBrief = { session_id: string; student_id: string };
        const ratedSet = new Set(
          ((evalData ?? []) as NoteBrief[]).map((e) => `${e.session_id}:${e.student_id}`)
        );
        type EnrollPast = { session_id: string; user_id: string; attendance: boolean | null };
        const sessMap: Record<string, { attend_pending: number; rate_pending: number }> = {};

        for (const e of (enrollData ?? []) as EnrollPast[]) {
          if (!sessMap[e.session_id])
            sessMap[e.session_id] = { attend_pending: 0, rate_pending: 0 };
          if (e.attendance === null) sessMap[e.session_id].attend_pending++;
          if (!ratedSet.has(`${e.session_id}:${e.user_id}`)) sessMap[e.session_id].rate_pending++;
        }

        // Todas las sesiones con trabajo pendiente (sin recortar): el contador usa
        // este total real. La lista se recorta a MAX_VISIBLE_TASKS solo al pintar,
        // ordenada por fecha ascendente (las más atrasadas primero).
        const allPending = pastSessions
          .filter(
            (s) =>
              sessMap[s.id] && (sessMap[s.id].attend_pending > 0 || sessMap[s.id].rate_pending > 0)
          )
          .slice()
          .sort((a, b) =>
            a.date === b.date
              ? a.time_start.localeCompare(b.time_start)
              : a.date.localeCompare(b.date)
          );

        setPendingTotal(allPending.length);

        const tasks: PendingTask[] = allPending.slice(0, MAX_VISIBLE_TASKS).map((s) => ({
          id: s.id,
          date: s.date,
          time_start: s.time_start,
          time_end: s.time_end,
          group_name: s.title,
          pending_attendance: sessMap[s.id]?.attend_pending ?? 0,
          pending_rating: sessMap[s.id]?.rate_pending ?? 0,
        }));
        setPendingTasks(tasks);
      }
    }

    // PR-EV1: evaluaciones formales pendientes = alumnos con ≥8 sesiones
    // asistidas desde su última evaluación (RPC).
    const { data: pendingFormal, error: errFormal } = await supabase.rpc(
      'formal_evaluations_pending_for_trainer',
      { p_trainer_id: profile.id }
    );
    if (errFormal) fallidos.push(QUE.evaluaciones);
    const cola = (pendingFormal ?? []) as PendienteDeEvaluar[];
    setPendingEvals(errFormal ? null : cola.length);
    // Cuántas se las ha puesto dirección. El total no lo distingue, y un
    // encargo con fecha no es lo mismo que un contador que sube (mig 288/311).
    setPendingAsignadas(errFormal ? 0 : cuantasAsignadas(cola));

    setBloquesFallidos(fallidos);
    setLoading(false);
  }, [profile]);

  useEffect(() => {
    load();
  }, [load]);

  // El desglose de la cola, uno para las dos tarjetas que lo enseñan. `null`
  // cuando no hay cola —ni contada ni contable—: no hay nada que desglosar y la
  // tarjeta grande tampoco sale.
  const desglose = desgloseDeLaCola(pendingEvals, pendingAsignadas);

  const formatTime = (t: string) => t.slice(0, 5);
  const formatDate = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gold-primary font-oswald animate-pulse">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {bloquesFallidos.length > 0 && (
        <div
          className="card border-error/40 bg-error/5"
          data-testid="dashboard-fallidos"
          role="status"
        >
          <p className="text-error text-sm">
            No se han podido cargar {listaEnCastellano([...bloquesFallidos])}. Lo que veas en «—» no
            es un cero: es que no se ha podido contar.
          </p>
        </div>
      )}

      {/* Welcome */}
      <div>
        <h2 className="font-oswald text-3xl uppercase tracking-wider text-white">
          Hola, {profile?.full_name?.split(' ')[0]}
        </h2>
        <p className="text-text-secondary font-inter text-sm mt-1">
          {new Date().toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          })}
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center gap-3 mb-2">
            <Calendar size={20} className="text-gold-primary" />
            <span className="text-text-secondary font-inter text-sm">Sesiones hoy</span>
          </div>
          <p className="font-oswald text-4xl text-white" data-testid="dashboard-hoy-count">
            {hoyIlegible ? '—' : todaySessions.length}
          </p>
        </div>
        <div className="card">
          <div className="flex items-center gap-3 mb-2">
            <ClipboardCheck size={20} className="text-[#FF9800]" />
            <span className="text-text-secondary font-inter text-sm">Tareas pendientes</span>
          </div>
          <p className="font-oswald text-4xl text-white" data-testid="pending-tasks-count">
            {pendingTotal ?? '—'}
          </p>
        </div>
        <div className="card">
          <div className="flex items-center gap-3 mb-2">
            <ClipboardList size={20} className="text-[#9C27B0]" />
            <span className="text-text-secondary font-inter text-sm">Evaluaciones pendientes</span>
          </div>
          <p className="font-oswald text-4xl text-white" data-testid="dashboard-evals-count">
            {pendingEvals ?? '—'}
          </p>
          {/* El número solo valía por dos cosas distintas a la vez: un encargo
              de dirección y un alumno que acumuló sesiones se sumaban sin
              decirlo. El desglose escribe LOS DOS términos, el cero incluido:
              una línea que no se pinta no dice «ninguna», no dice nada. */}
          {desglose && (
            <p
              className="font-inter text-text-secondary text-xs mt-1"
              data-testid="dashboard-evals-desglose"
            >
              {desglose}
            </p>
          )}
        </div>
      </div>

      {/* Today's sessions */}
      <div>
        <h3 className="font-oswald text-lg uppercase tracking-widest text-text-secondary mb-4">
          Sesiones de hoy
        </h3>
        {hoyIlegible ? (
          // El visto verde afirmaba que la jornada está despejada. Con la
          // lectura caída no se sabe, y es justo el día que el entrenador
          // consulta para saber si tiene que ir.
          <div className="card flex items-center gap-3" data-testid="dashboard-hoy-ilegible">
            <AlertTriangle size={18} className="text-error" />
            <span className="font-inter text-sm text-error">
              No se han podido leer las sesiones de hoy. Mírate la agenda antes de dar por hecho que
              no tienes ninguna.
            </span>
          </div>
        ) : todaySessions.length === 0 ? (
          <div className="card flex items-center gap-3 text-text-secondary">
            <CheckCircle size={18} className="text-[#4CAF50]" />
            <span className="font-inter text-sm">Sin sesiones hoy</span>
          </div>
        ) : (
          <div className="space-y-2">
            {todaySessions.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/monitor/sesion/${s.id}`)}
                className="card w-full text-left hover:border-gold-primary/40 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[48px]">
                      <p className="font-oswald text-gold-primary text-lg leading-none">
                        {formatTime(s.time_start)}
                      </p>
                      <p className="font-inter text-text-secondary text-xs">
                        –{formatTime(s.time_end)}
                      </p>
                    </div>
                    <div>
                      <p className="font-oswald text-white text-base">{s.group_name}</p>
                      <p className="font-inter text-text-secondary text-xs mt-0.5">
                        {s.confirmed} alumnos
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* ⚠️ LA PRECEDENCIA, COPIADA DE LA PANTALLA DEL ALUMNO: «la
                        sesión cancelada por el club manda sobre el estado del
                        enrollment». Aquí ese estado es este badge. Y la sesión
                        NO se oculta: hay que verla para entender por qué está
                        vacía. */}
                    {s.cancelada ? (
                      <span
                        data-testid={`dashboard-cancelada-${s.id}`}
                        className="flex items-center gap-1.5 text-xs font-inter rounded-lg px-3 py-1.5"
                        style={{
                          color: COLORES_CANCELADA.text,
                          backgroundColor: COLORES_CANCELADA.bg,
                          border: `1px solid ${COLORES_CANCELADA.border}`,
                        }}
                      >
                        <XCircle size={12} /> {ETIQUETA_CANCELADA}
                      </span>
                    ) : s.pending > 0 ? (
                      <span
                        data-testid={`dashboard-pend-${s.id}`}
                        className="flex items-center gap-1.5 text-[#FF9800] text-xs font-inter bg-[#FF9800]/10 border border-[#FF9800]/30 rounded-lg px-3 py-1.5"
                      >
                        <Clock size={12} /> {s.pending} pend.
                      </span>
                    ) : (
                      <span
                        data-testid={`dashboard-listo-${s.id}`}
                        className="flex items-center gap-1.5 text-[#4CAF50] text-xs font-inter bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-lg px-3 py-1.5"
                      >
                        <CheckCircle size={12} /> Listo
                      </span>
                    )}
                    <span className="text-text-secondary group-hover:text-gold-primary transition-colors text-sm">
                      →
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Pending tasks */}
      {(pendingTotal ?? 0) > 0 && (
        <div>
          <h3 className="font-oswald text-lg uppercase tracking-widest text-text-secondary mb-4">
            Sesiones con tareas pendientes
          </h3>
          <div className="space-y-2">
            {pendingTasks.map((t) => (
              <button
                key={t.id}
                onClick={() => navigate(`/monitor/sesion/${t.id}`)}
                className="card w-full text-left hover:border-gold-primary/40 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-oswald text-white text-base">{t.group_name}</p>
                    <p className="font-inter text-text-secondary text-xs mt-0.5 capitalize">
                      {formatDate(t.date)} · {formatTime(t.time_start)}–{formatTime(t.time_end)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {t.pending_attendance > 0 && (
                      <span className="flex items-center gap-1.5 text-[#FF9800] text-xs font-inter bg-[#FF9800]/10 border border-[#FF9800]/30 rounded-lg px-2.5 py-1">
                        <ClipboardCheck size={12} /> {t.pending_attendance} asist.
                      </span>
                    )}
                    {t.pending_rating > 0 && (
                      <span className="flex items-center gap-1.5 text-[#2196F3] text-xs font-inter bg-[#2196F3]/10 border border-[#2196F3]/30 rounded-lg px-2.5 py-1">
                        <Star size={12} /> {t.pending_rating} valor.
                      </span>
                    )}
                    <span className="text-text-secondary group-hover:text-gold-primary transition-colors text-sm">
                      →
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {(pendingTotal ?? 0) > pendingTasks.length && (
            <button
              type="button"
              onClick={() => navigate('/monitor/agenda?filter=past')}
              data-testid="pending-tasks-footer"
              className="mt-3 w-full text-left font-inter text-sm text-text-secondary hover:text-gold-primary transition-colors"
            >
              Mostrando {pendingTasks.length} de {pendingTotal} · ver sesiones pasadas en Agenda →
            </button>
          )}
        </div>
      )}

      {/* Eval reminder */}
      {pendingEvals === null && (
        <div
          className="card border-error/40 bg-error/5"
          data-testid="dashboard-evals-ilegible"
          role="status"
        >
          <p className="text-error text-sm">
            No se ha podido comprobar si tienes evaluaciones formales pendientes. Este recordatorio
            solo sale cuando hay alguna, así que no verlo no quiere decir que no haya.
          </p>
        </div>
      )}
      {(pendingEvals ?? 0) > 0 && (
        <div
          className="card border-[#9C27B0]/30 bg-[#9C27B0]/5 cursor-pointer hover:border-[#9C27B0]/50 transition-colors"
          onClick={() => navigate('/monitor/evaluaciones')}
        >
          <div className="flex items-center gap-3">
            <ClipboardList size={20} className="text-[#9C27B0]" />
            <div className="flex-1">
              {/* El titular dice lo que ES. Antes era «3 alumnos sin evaluar
                  este mes» —el lenguaje del umbral— encima de un total que
                  incluye encargos: con 2 asignadas de 3 afirmaba que tres
                  alumnos habían acumulado sesiones, y a dos se los había
                  mandado dirección. El texto vive en el gemelo. */}
              <p className="font-oswald text-white text-base" data-testid="dashboard-evals-titular">
                {titularDeLaCola(pendingEvals ?? 0, pendingAsignadas)}
              </p>
              {desglose && (
                <p
                  className="font-inter text-gold-primary text-xs mt-0.5"
                  data-testid="dashboard-evals-asignadas"
                >
                  {desglose}
                </p>
              )}
              <p className="font-inter text-text-secondary text-xs mt-0.5">Ir a Evaluaciones →</p>
            </div>
          </div>
        </div>
      )}

      {/* PF sin planificar (48h): aviso que antes solo salía dentro de la
          pantalla de Preparación Física. Se carga fuera del early-return por
          "sin grupos" → un entrenador solo-PF también lo ve. */}
      {pfUnplanned === null && (
        <div
          className="card border-error/40 bg-error/5"
          data-testid="pf-sin-planificar-ilegible"
          role="status"
        >
          <p className="text-error text-sm">
            No se ha podido comprobar si tienes jugadores de Prep. Física sin planificar. Esta
            tarjeta solo aparece cuando hay alguno, así que su ausencia no vale como respuesta.
          </p>
        </div>
      )}
      {(pfUnplanned ?? 0) > 0 && (
        <div
          className="card border-[#EF4444]/30 bg-[#EF4444]/5 cursor-pointer hover:border-[#EF4444]/50 transition-colors"
          data-testid="pf-sin-planificar-card"
          onClick={() => navigate('/monitor/gym')}
        >
          <div className="flex items-center gap-3">
            <CalendarClock size={20} className="text-[#EF4444]" />
            <div className="flex-1">
              <p className="font-oswald text-white text-base">
                {pfUnplanned} jugador{pfUnplanned !== 1 ? 'es' : ''} de Prep. Física sin planificar
                (48h)
              </p>
              <p className="font-inter text-text-secondary text-xs mt-0.5">
                Planifica sus sesiones (o márcalas como descanso) →
              </p>
            </div>
          </div>
        </div>
      )}

      {/* FB-7 — Panel de análisis de vídeo del entrenador (se auto-oculta si no
          tiene reservas). Carga sus propios datos, independiente del load() del
          dashboard (que retorna pronto si el entrenador no tiene grupos). */}
      <MonitorVideoAnalysisPanel />
    </div>
  );
}
